<script setup>
// EXIF Viewer / Remover（nuxt#19 P3）。复刻 design 的两份 inline <script>：
//  - exif-viewer：FileReader→ArrayBuffer→DataView 手动解析 JPEG APP1/Exif 段（无 npm exif 库），列出相机/曝光/日期/GPS。
//  - exif-remover：把图片画进 canvas 再 toBlob 重编码，丢弃所有 EXIF（像素不变），下载干净图。
// 纯浏览器：FileReader / DataView / Canvas / URL.createObjectURL。复用 freetools.css 的 dropzone/workspace/ws-grid 结构。
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useAuthModals } from "@/composables/useAuthModals";
import { usePaywallModal } from "@/composables/usePaywallModal";
import { useBatch } from "@/composables/useBatch.js";
import { tierOf, fileTooLargeMsg } from "@/settings/free-tools/batch-limits.js";

const props = defineProps({
  mode: { type: String, default: "viewer" }, // viewer | remover
  slug: { type: String, default: "" },
});

const isRemover = computed(() => props.mode === "remover");

const { downloadBlob } = useFreeToolsDownload();

const store = useStore();

const stage = ref("drop"); // drop | work
const dragging = ref(false);
const fileInput = ref(null);

const fileName = ref("photo.jpg");
const fileSizeLabel = ref("—");
const previewUrl = ref("");

// viewer
const rows = ref([]); // [[k,v],...]
const noExif = ref(false);

// remover
let cleanBlob = null;
let cleanUrl = "";
let baseName = "photo";
let outType = "image/jpeg";

/* ---------- 批量模式(nuxt#21):remover 多文件 → 队列(纯前端 canvas 重编码丢 EXIF)。viewer 不批量。 ---------- */
const batchMode = ref(false);
const tierB = computed(() => tierOf(!!store.state.user.token, store.state.user.userInfo));
const tokenB = computed(() => store.state.user.token || "");
const userIdB = computed(() => (store.state.user.userInfo && store.state.user.userInfo.user_info_id) || "");
const _auth = useAuthModals();
const _paywall = usePaywallModal();
const batch = useBatch({
  category: "image",
  client: true, // 纯前端:浏览器内 canvas 重编码,无后端
  tier: tierB,
  token: tokenB,
  userId: userIdB,
  clientConvert: (file) => convertFileBatch(file),
  buildQuery: () => "",
  onSignup: () => _auth.openSignInModal(),
  onUpgrade: () => _paywall.openPaywall(["DST", "PES", "EXP", "JEF", "VP3", "HUS", "XXX", "EMD"]),
});

// 镜像单文件 remover 的 canvas 重编码逻辑,但输入是任意 file(不依赖模块级 outType/baseName)。
// 每个文件按自身类型决定输出:png→png / webp→webp / 其余→jpeg(铺白底,丢弃 EXIF/GPS)。
async function convertFileBatch(file) {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error("Could not read this image"));
      im.src = url;
    });
    const ot = file.type === "image/png" ? "image/png" : file.type === "image/webp" ? "image/webp" : "image/jpeg";
    const c = document.createElement("canvas");
    c.width = image.naturalWidth;
    c.height = image.naturalHeight;
    const cx = c.getContext("2d");
    if (ot === "image/jpeg") {
      cx.fillStyle = "#fff";
      cx.fillRect(0, 0, c.width, c.height);
    }
    cx.drawImage(image, 0, 0); // canvas 重绘 → toBlob 重编码,所有 EXIF(GPS/相机/时间戳)被丢弃,像素不变
    const blob = await new Promise((r) => c.toBlob(r, ot, 0.95));
    if (!blob) throw new Error("Conversion failed");
    const ext = ot === "image/png" ? "png" : ot === "image/webp" ? "webp" : "jpg";
    return { blob, ext };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function enterBatch(files) {
  batchMode.value = true;
  batch.clearAll();
  batch.addFiles(files);
}
function addMoreFiles() {
  if (fileInput.value) fileInput.value.click();
}
// 队列清空 → 退出批量回到 dropzone;套餐(登录/升级)变化 → 重算锁定。
watch(() => batch.items.value.length, (n) => {
  if (batchMode.value && n === 0) batchMode.value = false;
});
watch([tierB], () => batch.reevaluate());

const accept = computed(() =>
  isRemover.value ? "image/jpeg,image/png,image/webp" : "image/jpeg,image/tiff"
);
const formats = computed(() =>
  isRemover.value ? ["JPG", "PNG", "WEBP"] : ["JPG", "JPEG", "TIFF"]
);

function fmtBytes(b) {
  if (b < 1024) return b + " B";
  if (b < 1048576) return (b / 1024).toFixed(1) + " KB";
  return (b / 1048576).toFixed(2) + " MB";
}

/* ---- 手动 JPEG EXIF 解析（复刻 blueprint parseExif，DataView 逐段扫描 APP1/Exif TIFF/IFD）---- */
function parseExif(buf) {
  const dv = new DataView(buf);
  if (dv.getUint16(0) !== 0xffd8) return null; // not JPEG
  let off = 2;
  const len = dv.byteLength;
  while (off < len) {
    const marker = dv.getUint16(off);
    off += 2;
    if (marker === 0xffe1) {
      // APP1
      off += 2; // skip segment size
      if (dv.getUint32(off) !== 0x45786966) return null; // "Exif"
      off += 6; // "Exif\0\0"
      const tiff = off;
      const little = dv.getUint16(tiff) === 0x4949;
      const u16 = (o) => dv.getUint16(o, little);
      const u32 = (o) => dv.getUint32(o, little);
      const ifd0 = tiff + u32(tiff + 4);
      const tags = {};
      function readIFD(start) {
        const n = u16(start);
        for (let i = 0; i < n; i++) {
          const e = start + 2 + i * 12;
          const tag = u16(e);
          const type = u16(e + 2);
          const cnt = u32(e + 4);
          let valOff = e + 8;
          const typeSize = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 9: 4, 10: 8 }[type] || 1;
          const total = typeSize * cnt;
          if (total > 4) valOff = tiff + u32(e + 8);
          let v;
          if (type === 2) {
            let s = "";
            for (let j = 0; j < cnt - 1; j++) s += String.fromCharCode(dv.getUint8(valOff + j));
            v = s.trim();
          } else if (type === 3) v = u16(valOff);
          else if (type === 4) v = u32(valOff);
          else if (type === 5) v = u32(valOff) / u32(valOff + 4);
          else v = u32(valOff);
          tags[tag] = { v, type, cnt, valOff };
        }
        return u32(start + 2 + n * 12);
      }
      readIFD(ifd0);
      // EXIF sub-IFD
      if (tags[0x8769]) readIFD(tiff + tags[0x8769].v);
      const NAME = {
        0x010f: "Make",
        0x0110: "Model",
        0x0131: "Software",
        0x0132: "DateTime",
        0x9003: "Date taken",
        0x829a: "Exposure",
        0x829d: "F-number",
        0x8827: "ISO",
        0x920a: "Focal length",
        0x0112: "Orientation",
      };
      const out = {};
      for (const t in NAME) {
        const key = Number(t);
        if (tags[key] != null) {
          let val = tags[key].v;
          if (key === 0x829a && val < 1) val = "1/" + Math.round(1 / val) + "s";
          else if (key === 0x829d) val = "f/" + val;
          else if (key === 0x920a) val = val + "mm";
          out[NAME[t]] = val;
        }
      }
      out["GPS data"] = tags[0x8825] ? "Present ⚠️" : "None";
      return out;
    } else if ((marker & 0xff00) !== 0xff00) {
      break;
    } else {
      off += dv.getUint16(off);
    }
  }
  return null;
}

/* ---- remover：canvas 重编码丢弃 EXIF ---- */
function buildClean(img) {
  const c = document.createElement("canvas");
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const cx = c.getContext("2d");
  if (outType === "image/jpeg") {
    cx.fillStyle = "#fff";
    cx.fillRect(0, 0, c.width, c.height);
  }
  cx.drawImage(img, 0, 0);
  c.toBlob(
    (b) => {
      cleanBlob = b;
    },
    outType,
    0.95
  );
}

function revokePreview() {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = "";
  }
}
function revokeClean() {
  if (cleanUrl) {
    URL.revokeObjectURL(cleanUrl);
    cleanUrl = "";
  }
}

function loadFile(file) {
  if (!file) return;
  if (isRemover.value && !file.type.startsWith("image/")) {
    alert("Please choose an image.");
    return;
  }
  const _m = fileTooLargeMsg(file, "image");
  if (_m) { alert(_m); return; }
  fileName.value = file.name;
  baseName = file.name.replace(/\.[^.]+$/, "") || "photo";
  outType =
    file.type === "image/png" ? "image/png" : file.type === "image/webp" ? "image/webp" : "image/jpeg";

  revokePreview();
  revokeClean();
  const url = URL.createObjectURL(file);
  previewUrl.value = url;

  const img = new Image();
  img.onload = () => {
    if (isRemover.value) {
      fileSizeLabel.value = fmtBytes(file.size) + " · original";
      cleanBlob = null;
      buildClean(img);
      stage.value = "work";
    } else {
      fileSizeLabel.value = fmtBytes(file.size);
      const r = new FileReader();
      r.onload = () => {
        let ex = null;
        try {
          ex = parseExif(r.result);
        } catch (e) {
          ex = null;
        }
        const out = [
          ["Dimensions", img.naturalWidth + " × " + img.naturalHeight + " px"],
          ["File size", fmtBytes(file.size)],
          ["Type", file.type || "image"],
        ];
        if (ex) for (const k in ex) out.push([k, String(ex[k])]);
        rows.value = out;
        noExif.value = !ex;
        stage.value = "work";
      };
      r.readAsArrayBuffer(file);
    }
  };
  img.onerror = () => {
    alert("Could not read this image.");
  };
  img.src = url;
}

function pick() {
  fileInput.value && fileInput.value.click();
}
// remover 支持批量(多文件→队列);viewer 不批量,多文件只取第一个走单文件解析。
function handleFiles(files) {
  if (!files || !files.length) return;
  if (!isRemover.value) {
    loadFile(files[0]); // viewer:始终单文件
    return;
  }
  if (batchMode.value) { batch.addFiles(files); return; } // 已在批量 → add more
  if (files.length > 1) { enterBatch(files); return; }     // 多文件 → 进批量
  loadFile(files[0]);                                       // 单文件 → 原有单文件流
}
function onInput(e) {
  handleFiles(e.target.files);
  e.target.value = ""; // 允许 add more / 重选同名文件
}
function onDrop(e) {
  e.preventDefault();
  dragging.value = false;
  handleFiles(e.dataTransfer && e.dataTransfer.files);
}
function reset() {
  stage.value = "drop";
  rows.value = [];
  noExif.value = false;
  cleanBlob = null;
  revokePreview();
  revokeClean();
  if (fileInput.value) fileInput.value.value = "";
}

function download() {
  if (!cleanBlob) return;
  revokeClean();
  const ext = outType === "image/png" ? "png" : outType === "image/webp" ? "webp" : "jpg";
  downloadBlob(cleanBlob, baseName + "-clean." + ext);
}

onBeforeUnmount(() => {
  revokePreview();
  revokeClean();
});
</script>

<template>
  <div class="exif">
    <div class="tool-stage">
      <input ref="fileInput" type="file" :accept="accept" :multiple="isRemover" hidden @change="onInput" />

      <!-- 拖放区 -->
      <div
        v-show="stage === 'drop' && !batchMode"
        class="dropzone"
        :class="{ drag: dragging }"
        @click="pick"
        @dragenter.prevent="dragging = true"
        @dragover.prevent="dragging = true"
        @dragleave.prevent="dragging = false"
        @drop="onDrop"
      >
        <div class="dz-ico">
          <svg v-if="isRemover" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /><path d="M9 12l2 2 4-4" />
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
          </svg>
        </div>
        <h3>{{ isRemover ? "Drop your photo here" : "Drop a JPG photo here" }}</h3>
        <div class="hint">
          {{ isRemover ? "or click to browse — JPG, PNG, WEBP" : "or click to browse — JPG photos carry the most EXIF data" }}
        </div>
        <div class="formats"><span v-for="f in formats" :key="f">{{ f }}</span></div>
        <div class="dz-or">
          {{ isRemover ? "Cleaned right in your browser — never stored" : "Read right in your browser — never stored" }}
        </div>
        <button class="btn btn-amber dz-btn" type="button" @click.stop="pick">Choose Photo</button>
      </div>

      <!-- batch 多文件队列（nuxt#21）：remover 拖入 >1 个文件时进入；纯前端 canvas 重编码丢 EXIF -->
      <div v-if="batchMode" class="workspace show">
        <BatchQueue :batch="batch" category="image" :client="true" :file-count-max="20" @add-more="addMoreFiles">
          <template #params>
            <div class="ctl">
              <label>Metadata removal</label>
              <p class="note" style="margin: 0">
                Every photo is re-encoded in your browser, discarding all EXIF metadata — GPS coordinates, camera make/model, software and timestamps. The pixels stay identical.
              </p>
            </div>
            <div style="font-size: 12.5px; color: var(--tx-faint); font-weight: 600">
              One set of settings applies to all files.
            </div>
          </template>
        </BatchQueue>
      </div>

      <!-- 工作区（单文件） -->
      <div v-if="!batchMode" class="workspace" :class="{ show: stage === 'work' }">
        <div class="ws-grid">
          <div class="ws-preview"><img :src="previewUrl" alt="preview" /></div>
          <div class="ws-panel">
            <div class="ws-file">
              <img class="thumb" :src="previewUrl" alt="" />
              <div class="meta">
                <div class="fn">{{ fileName }}</div>
                <div class="fsz">{{ fileSizeLabel }}</div>
              </div>
              <span class="rm" title="Remove" @click="reset">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg>
              </span>
            </div>

            <!-- viewer: 元数据表 -->
            <template v-if="!isRemover">
              <div class="ctl">
                <label>Metadata</label>
                <div class="exif-tbl">
                  <div v-for="(row, i) in rows" :key="i" class="row">
                    <span class="k">{{ row[0] }}</span><span class="v">{{ row[1] }}</span>
                  </div>
                  <div v-if="noExif" class="exif-empty">
                    No EXIF metadata found. This image has no embedded camera, date or GPS data (it may already be clean, a PNG, or stripped on upload by a website).
                  </div>
                </div>
              </div>
              <div class="ws-actions">
                <button class="btn btn-outline" type="button" style="margin-top: auto" @click="reset">Check another photo</button>
                <NuxtLink class="btn btn-amber" to="/free-tools/exif-remover">Remove this metadata →</NuxtLink>
              </div>
            </template>

            <!-- remover: 结果 + 下载 -->
            <template v-else>
              <div class="result-pill show">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /><path d="M9 12l2 2 4-4" /></svg>
                <span>All metadata removed — <span class="big">GPS, camera &amp; date stripped</span></span>
              </div>
              <p class="note">
                Your photo is re-encoded in your browser, which discards all EXIF metadata including GPS coordinates, camera make/model, software and timestamps — the pixels stay identical.
              </p>
              <div class="ws-actions">
                <button class="btn btn-amber btn-lg" type="button" @click="download">Download clean photo</button>
                <button class="btn btn-outline" type="button" @click="reset">Clean another</button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="trust">
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>{{ isRemover ? "Private — files stay on your device" : "Private — read on your device" }}</span>
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>{{ isRemover ? "Removes GPS & camera data" : "Camera, date & GPS" }}</span>
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Unlimited &amp; free</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.exif { width: 100%; }
.tool-stage { max-width: 860px; margin: 0 auto; }
.ws-actions { display: flex; flex-direction: column; gap: 10px; margin-top: auto; }
.ctl label { font-size: 12px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--tx-faint); display: block; margin-bottom: 10px; }
.note { font-size: 12.5px; color: var(--tx-faint); line-height: 1.5; }

/* EXIF 表（复刻 viewer inline <style>） */
.exif-tbl { display: flex; flex-direction: column; gap: 1px; background: var(--line); border: 1px solid var(--line); border-radius: 11px; overflow: hidden; }
.exif-tbl .row { display: flex; justify-content: space-between; gap: 14px; padding: 11px 14px; background: var(--surface-2); font-size: 13.5px; }
.exif-tbl .k { color: var(--tx-faint); font-weight: 600; }
.exif-tbl .v { color: var(--tx); font-weight: 600; font-family: var(--font-h); text-align: right; word-break: break-word; }
.exif-empty { color: var(--tx-faint); font-size: 13.5px; text-align: center; padding: 18px; line-height: 1.5; background: var(--surface-2); }

.trust { display: flex; gap: 22px; justify-content: center; flex-wrap: wrap; margin-top: 22px; }
.trust .i { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: var(--tx-dim); }
.trust .i svg { width: 16px; height: 16px; color: #fbbf24; }

@media (max-width: 760px) {
  .ws-grid { grid-template-columns: 1fr; }
  .ws-panel { border-left: none; border-top: 1px solid var(--line); }
}
</style>
